import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "../components/Layout";
import { api, formatINR, timeAgo } from "../lib/api";
import { downloadDoc } from "../lib/files";
import { Upload, CheckCircle, Flame, AlertTriangle, Split, Shield, FileText, Download, Eye, HardDrive, ExternalLink } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import EditRequestDialog from "../components/EditRequestDialog";

const STATUS_COLORS = {
  pending: "bg-amber-500 text-black",
  approved: "bg-slate-700 text-white",
  rejected: "bg-slate-500 text-white",
  paid: "bg-olive text-white",
  recorded: "bg-olive text-white",
  closed: "bg-black text-white",
};

// Local thin wrapper — all behaviour (specific error toasts + Drive fallback)
// lives in the shared downloadDoc helper.
const openDocBlob = (url, filename, asDownload, driveLink = null) =>
  downloadDoc({ url, filename, asDownload, driveLink });

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [billBlob, setBillBlob] = useState(null);
  const [driveStatus, setDriveStatus] = useState(null);
  // Phase F4 — edit dialog state.
  const [editOpen, setEditOpen] = useState(false);
  const fileRef = useRef(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/requests/${id}`);
      setReq(r.data);
      if (r.data.bill_url) {
        const blob = await api.get(r.data.bill_url.replace(/^\/api/, ""), { responseType: "blob" });
        setBillBlob(URL.createObjectURL(blob.data));
      }
    } catch (e) {
      toast.error("Request not found");
      navigate("/dashboard");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [id]);

  // Admin-only: fetch Drive upload diagnostics. Failures here are non-fatal.
  useEffect(() => {
    if (!req || user?.role !== "admin") return;
    let cancelled = false;
    api.get(`/requests/${id}/drive-status`)
      .then((r) => { if (!cancelled) setDriveStatus(r.data); })
      .catch(() => { if (!cancelled) setDriveStatus(null); });
    return () => { cancelled = true; };
  }, [req, user, id]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post(`/requests/${id}/bill`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bill uploaded");
      fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const close = async () => {
    try {
      await api.patch(`/requests/${id}/close`);
      toast.success("Request closed");
      fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Close failed");
    }
  };

  const unflagPostFacto = async () => {
    if (!window.confirm("Remove the post-facto flag from this request?")) return;
    try {
      await api.patch(`/admin/requests/${id}/unflag-postfacto`);
      toast.success("Post-facto flag removed");
      fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  if (loading || !req) return (
    <AppShell back="/dashboard" title="REQUEST"><div className="p-5 label-micro">LOADING…</div></AppShell>
  );

  const flags = req.flags || {};
  const canClose = req.status === "paid" && (user?.role === "admin" || !!user?.permissions_effective?.execute_payments);
  // Phase F4 — admin-only edit while pre-payment. Server enforces the same
  // gate; the button is just a UI shortcut.
  const canEdit = user?.role === "admin" && ["pending", "approved"].includes(req.status);
  // Payment auto-records expense now; close action deprecated but retained for legacy paid-status docs

  return (
    <AppShell back="/dashboard" title={req.request_id}>
      <div className="px-5 py-5 space-y-6">
        {/* Status strip */}
        <div className="flex justify-between items-center">
          <span
            data-testid="request-status-pill"
            className={`font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 ${STATUS_COLORS[req.status]}`}
          >
            {req.status}
          </span>
          <div className="flex items-center gap-3">
            {canEdit && (
              <button
                data-testid="edit-request-btn"
                onClick={() => setEditOpen(true)}
                className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition"
              >
                Edit
              </button>
            )}
            <div className="label-micro">{timeAgo(req.created_at)}</div>
          </div>
        </div>

        {/* Vendor + amount hero */}
        <div className="bg-white border border-slate-200 p-5">
          <div className="label-micro">VENDOR</div>
          <div className="font-semibold text-2xl tracking-tight uppercase mb-3">{req.vendor_name}</div>
          <div className="label-micro">REQUESTED</div>
          <div className="font-semibold text-5xl tracking-tight">{formatINR(req.amount)}</div>
          <div className="grid grid-cols-2 gap-0 border-t border-slate-200 mt-3 pt-3">
            <div>
              <div className="label-micro">APPROVED</div>
              <div className="font-semibold">{req.approved_amount ? formatINR(req.approved_amount) : "—"}</div>
            </div>
            <div>
              <div className="label-micro">PAID</div>
              <div className="font-semibold">{req.paid_amount ? formatINR(req.paid_amount) : "—"}</div>
            </div>
          </div>
        </div>

        {/* Flags */}
        {(req.urgency === "emergency" || flags.post_facto || flags.split || flags.sla_breach) && (
          <div className="flex flex-wrap gap-1.5 items-center">
            {req.urgency === "emergency" && <span className="inline-flex items-center gap-1 bg-amber-500 text-black px-2 py-1 font-mono text-[10px] uppercase tracking-widest font-bold"><Flame size={12}/>EMERGENCY</span>}
            {flags.post_facto && (
              <>
                <span className="inline-flex items-center gap-1 bg-red-600 text-white px-2 py-1 font-mono text-[10px] uppercase tracking-widest font-bold"><AlertTriangle size={12}/>POST-FACTO</span>
                {user?.role === "admin" && (
                  <button
                    data-testid="unflag-postfacto-btn"
                    onClick={unflagPostFacto}
                    className="inline-flex items-center gap-1 border border-black bg-white px-2 py-1 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white"
                  >
                    <Shield size={10}/>REMOVE FLAG
                  </button>
                )}
              </>
            )}
            {flags.split && <span className="inline-flex items-center gap-1 bg-red-600 text-white px-2 py-1 font-mono text-[10px] uppercase tracking-widest font-bold"><Split size={12}/>SPLIT</span>}
            {flags.sla_breach && <span className="inline-flex items-center gap-1 bg-red-600 text-white px-2 py-1 font-mono text-[10px] uppercase tracking-widest font-bold"><AlertTriangle size={12}/>SLA BREACH</span>}
          </div>
        )}

        {/* Meta */}
        <div className="border-t border-slate-200">
          <Row label="SITE" value={req.site} />
          <Row label="CATEGORY" value={req.category} />
          {/* v2.12 — urgency / scheduled_date removed from the request schema.
              Legacy rows may still carry an `urgency` value, so we surface it
              only when present. New rows never populate it. */}
          {req.urgency && <Row label="URGENCY" value={String(req.urgency).toUpperCase()} />}
          {req.is_bulk && <Row label="TYPE" value="BULK PURCHASE" />}
          {req.scheduled_date && <Row label="SCHEDULED FOR" value={req.scheduled_date} />}
          <Row label="REQUESTED BY" value={req.created_by_name} />
          {req.purchase_time && <Row label="PURCHASE TIME" value={new Date(req.purchase_time).toLocaleString("en-IN")} />}
          {req.approved_by_name && <Row label="APPROVED BY" value={req.approved_by_name} />}
          {req.auto_approved_cash && (
            <Row
              label="APPROVAL TYPE"
              value={<span data-testid="auto-cash-badge" className="font-mono text-[11px] uppercase tracking-wider bg-olive/10 text-olive px-2 py-0.5 rounded">AUTO-APPROVED CASH</span>}
            />
          )}
          {req.payment_batch && <Row label="PAYMENT BATCH" value={req.payment_batch} />}
          {req.requested_payment_mode && <Row label="REQUESTED PAYMENT MODE" value={req.requested_payment_mode} />}
          {req.payment_mode && <Row label="PAYMENT MODE" value={req.payment_mode} />}
          {req.employee_name && <Row label="EMPLOYEE NAME" value={req.employee_name} />}
          {req.reject_reason && <Row label="REJECT REASON" value={req.reject_reason} />}
          {req.notes && <Row label="NOTES" value={req.notes} />}
        </div>

        {/* Bulk items breakdown */}
        {req.is_bulk && req.items && req.items.length > 0 && (
          <div>
            <div className="label-micro mb-2">ITEMS ({req.items.length})</div>
            <div className="bg-white border border-slate-200">
              {req.items.map((it, i) => (
                <div key={i} className="px-4 py-2.5 flex justify-between items-center border-b border-slate-200 last:border-b-0">
                  <span className="font-mono text-sm flex-1">
                    <span className="text-slate-400 mr-2">{String(i + 1).padStart(2, "0")}</span>{it.description}
                  </span>
                  <span className="font-semibold">{formatINR(it.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Supporting documents */}
        {req.supporting_docs && req.supporting_docs.length > 0 && (
          <div>
            <div className="label-micro mb-2">SUPPORTING DOCUMENTS ({req.supporting_docs.length})</div>
            <div className="space-y-1">
              {req.supporting_docs.map((d, i) => (
                <div
                  key={i}
                  data-testid={`support-doc-${i}`}
                  className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-2"
                >
                  <FileText size={14} className="text-slate-400 flex-shrink-0" />
                  <span className="font-mono text-xs truncate flex-1">{d.filename || `Document ${i + 1}`}</span>
                  <button
                    data-testid={`support-doc-view-${i}`}
                    onClick={() => openDocBlob(d.url, d.filename, false, d.drive_link)}
                    className="border border-black px-2 py-1 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white transition-colors flex items-center gap-1"
                    aria-label="View document"
                  >
                    <Eye size={11} /> VIEW
                  </button>
                  <button
                    data-testid={`support-doc-download-${i}`}
                    onClick={() => openDocBlob(d.url, d.filename, true, d.drive_link)}
                    className="border border-black px-2 py-1 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white transition-colors flex items-center gap-1"
                    aria-label="Download document"
                  >
                    <Download size={11} /> DL
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bill */}
        <div>
          <div className="label-micro mb-2">BILL / RECEIPT</div>
          {billBlob ? (
            <div className="border border-slate-200 p-2 bg-white">
              <img data-testid="bill-preview" src={billBlob} alt="bill" className="w-full max-h-96 object-contain" />
              <div className="flex gap-2 mt-2 pt-2 border-t border-slate-200">
                <button
                  data-testid="bill-view-btn"
                  onClick={() => openDocBlob(req.bill_url, "bill", false, req.bill_drive_link)}
                  className="flex-1 border border-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-1"
                >
                  <Eye size={11} /> OPEN
                </button>
                <button
                  data-testid="bill-download-btn"
                  onClick={() => openDocBlob(req.bill_url, "bill", true, req.bill_drive_link)}
                  className="flex-1 border border-black px-3 py-2 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-1"
                >
                  <Download size={11} /> DOWNLOAD
                </button>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-slate-300 p-6 text-center">
              <div className="font-mono text-xs text-slate-500 uppercase tracking-widest">NO BILL UPLOADED</div>
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={onFile}
            data-testid="bill-input"
          />
          <button
            data-testid="upload-bill-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="mt-3 w-full bg-white border border-slate-200 py-3 font-mono text-xs uppercase tracking-widest font-bold hover:bg-black hover:text-white transition-colors flex justify-center items-center gap-2"
          >
            <Upload size={14}/>{uploading ? "UPLOADING…" : req.bill_url ? "REPLACE BILL" : "UPLOAD BILL"}
          </button>
        </div>

        {/* Drive diagnostics — admin only */}
        {user?.role === "admin" && (
          <DriveDiagnostics status={driveStatus} />
        )}

        {/* Close */}
        {canClose && (
          <button
            data-testid="close-request-btn"
            onClick={close}
            className="w-full bg-black text-white py-4 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive transition-all flex justify-center items-center gap-2"
          >
            <CheckCircle size={16}/>MARK AS CLOSED
          </button>
        )}
      </div>
      {editOpen && (
        <EditRequestDialog
          req={req}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => { setReq(updated); setEditOpen(false); }}
        />
      )}
    </AppShell>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center py-3 border-b border-slate-200">
    <span className="label-micro">{label}</span>
    <span className="font-display font-bold text-sm text-right max-w-[60%] break-words">{value}</span>
  </div>
);

function DriveDiagnostics({ status }) {
  if (!status) {
    return (
      <div className="border-2 border-dashed border-slate-300 p-4 bg-slate-50" data-testid="drive-diagnostics-loading">
        <div className="flex items-center gap-2 label-micro">
          <HardDrive size={14}/> DRIVE STATUS · LOADING…
        </div>
      </div>
    );
  }
  const configured = status.drive_configured;
  const last = status.last_attempt;
  const attempts = status.attempts || [];
  const lastStatusLabel = last ? (last.status || "?").replace(/_/g, " ").toUpperCase() : "NO ATTEMPTS YET";
  const lastStatusColor = !last ? "bg-slate-100 text-slate-500"
    : last.status === "uploaded" ? "bg-olive text-white"
    : last.status === "skipped_not_configured" ? "bg-amber-200 text-amber-900"
    : "bg-red-600 text-white";

  return (
    <div className="border border-slate-200 bg-white" data-testid="drive-diagnostics">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-[#F7F8FA]">
        <div className="flex items-center gap-2">
          <HardDrive size={16}/>
          <div className="font-mono text-xs font-bold uppercase tracking-widest">GOOGLE DRIVE STATUS</div>
        </div>
        <span className={`font-mono text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${configured ? "bg-olive text-white" : "bg-amber-200 text-amber-900"}`}>
          {configured ? "CONNECTED" : "NOT CONFIGURED"}
        </span>
      </div>
      <div className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs font-mono">
          <Kv label="LAST UPLOAD STATUS">
            <span className={`px-2 py-0.5 ${lastStatusColor} font-bold tracking-widest`}>{lastStatusLabel}</span>
          </Kv>
          <Kv label="LAST ATTEMPT">{last ? new Date(last.attempted_at).toLocaleString("en-IN") : "—"}</Kv>
          <Kv label="FOLDER PATH">{last?.folder_path || "—"}</Kv>
          <Kv label="ATTEMPTS LOGGED">{status.attempt_count}</Kv>
        </div>
        {last?.error && (
          <div className="border border-red-300 bg-red-50 p-2 text-xs font-mono text-red-800" data-testid="drive-last-error">
            <span className="font-bold">ERROR:</span> {last.error}
          </div>
        )}
        {status.bill_drive_link && (
          <a
            href={status.bill_drive_link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="drive-bill-link"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest font-bold border border-black px-2 py-1 hover:bg-black hover:text-white"
          >
            <ExternalLink size={11}/> OPEN BILL IN DRIVE
          </a>
        )}
        {attempts.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer label-micro" data-testid="drive-history-toggle">
              UPLOAD HISTORY ({attempts.length})
            </summary>
            <div className="mt-2 border border-slate-200 max-h-64 overflow-y-auto">
              {attempts.map((a) => (
                <div key={a.attempt_id} className="px-3 py-2 border-b border-slate-200 last:border-b-0 flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[11px] truncate">{a.kind} · {a.filename}</div>
                    <div className="label-micro">{new Date(a.attempted_at).toLocaleString("en-IN")} · {a.folder_path || "—"}</div>
                    {a.error && <div className="font-mono text-[10px] text-red-700 mt-0.5">ERR: {a.error}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`font-mono text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${a.status === "uploaded" ? "bg-olive text-white" : a.status === "skipped_not_configured" ? "bg-amber-200 text-amber-900" : "bg-red-600 text-white"}`}>
                      {(a.status || "?").replace(/_/g, " ")}
                    </span>
                    {a.drive_link && (
                      <a href={a.drive_link} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] underline">OPEN</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

const Kv = ({ label, children }) => (
  <div className="flex justify-between gap-3 py-1 border-b border-slate-200 last:border-b-0">
    <span className="label-micro">{label}</span>
    <span className="font-mono text-xs text-right max-w-[60%] break-words">{children}</span>
  </div>
);
